/**
 * FERP Portal - Smart Entity Types
 *
 * Merges curated defaults with personalized access frequency data to produce
 * a prioritized entity type list for the SubgraphBrowserPage.
 *
 * 3-Layer Smart Navigation:
 * - Layer 1: Curated defaults (static per-subgraph, used when no usage data)
 * - Layer 2: Personalized (localStorage access counts reorder entity types)
 * - Layer 3: Discovery (future — LLM/Chaos suggestions)
 *
 * FERP Principles:
 * - Data-driven: Ordering from actual user behavior or curated metadata
 * - Dumb frontend: Just reorders, no business logic
 */

import { useMemo } from 'react';
import type { SubgraphEntityType } from '@/hooks/useTenantSubgraphConfig';
import { useEntityAccessTracker } from '@/hooks/useEntityAccessTracker';

/** Maximum number of entity types in the quick access / frequent section */
const MAX_QUICK_ACCESS = 8;

/**
 * Curated default entity types per subgraph.
 * Shown before the user has enough usage data for personalization.
 * Values are graphqlType names in priority order.
 */
const CURATED_DEFAULTS: Record<string, string[]> = {
  'fhir': ['Patient', 'Claim', 'Observation', 'Medication', 'Practitioner'],
  'payroll': ['PayrollRun', 'TimeEntry', 'PayStub', 'PayrollException'],
  'accounting': ['JournalEntry'],
};

/**
 * An entity type with smart navigation metadata attached.
 */
export interface SmartEntityType extends SubgraphEntityType {
  /** Whether this entity type is in the user's frequent list */
  isFrequent: boolean;
  /** Access count (0 if never accessed) */
  accessCount: number;
  /** Whether this entity type is a curated default */
  isCurated: boolean;
}

export interface UseSmartEntityTypesReturn {
  /** All entity types for this subgraph, in smart order */
  orderedEntityTypes: SubgraphEntityType[];
  /** The "frequently used" or "curated" entity types */
  frequentTypes: SmartEntityType[];
  /** All entity types with smart metadata */
  allSmartTypes: SmartEntityType[];
  /** Whether the user has personalization data */
  isPersonalized: boolean;
  /** Number of entity types shown in "quick access" (frequent or curated) */
  quickAccessCount: number;
}

/**
 * Merges curated defaults with personalized frequency data.
 *
 * Algorithm:
 * 1. If user has personalization data → top N by access count, rest alphabetical
 * 2. Else → curated defaults first, rest alphabetical
 * 3. Curated names not in entityTypes are silently skipped
 *
 * @param subgraphId - The subgraph to personalize for
 * @param entityTypes - Full entity type list from subgraph config
 */
export function useSmartEntityTypes(
  subgraphId: string,
  entityTypes: SubgraphEntityType[],
): UseSmartEntityTypesReturn {
  const tracker = useEntityAccessTracker();

  return useMemo(() => {
    const frequentRecords = tracker.getFrequentTypes(subgraphId);
    const isPersonalized = tracker.hasPersonalizationData(subgraphId);

    // Build lookup map: graphqlType -> SubgraphEntityType
    const typeMap = new Map(entityTypes.map((et) => [et.graphqlType, et]));

    // Determine priority list (names only)
    let priorityNames: string[];
    if (isPersonalized) {
      // Layer 2: Use actual user frequency data
      priorityNames = frequentRecords
        .slice(0, MAX_QUICK_ACCESS)
        .map((r) => r.entityType)
        .filter((name) => typeMap.has(name));
    } else {
      // Layer 1: Use curated defaults
      priorityNames = (CURATED_DEFAULTS[subgraphId] ?? [])
        .filter((name) => typeMap.has(name));
    }

    // Build access count lookup
    const frequentCountMap = new Map(
      frequentRecords.map((r) => [r.entityType, r.accessCount]),
    );

    // Mark which types are in the priority set
    const prioritySet = new Set(priorityNames);
    const curatedSet = new Set(
      isPersonalized ? [] : (CURATED_DEFAULTS[subgraphId] ?? []),
    );

    // Build smart types with metadata
    const allSmartTypes: SmartEntityType[] = entityTypes.map((et) => ({
      ...et,
      isFrequent: prioritySet.has(et.graphqlType),
      accessCount: frequentCountMap.get(et.graphqlType) ?? 0,
      isCurated: curatedSet.has(et.graphqlType),
    }));

    // Build ordered list: priority types first (in priority order), then rest alphabetical
    const priorityTypes = priorityNames
      .map((name) => typeMap.get(name))
      .filter((et): et is SubgraphEntityType => et !== undefined);

    const remainingTypes = entityTypes
      .filter((et) => !prioritySet.has(et.graphqlType))
      .sort((a, b) => a.pluralName.localeCompare(b.pluralName));

    const orderedEntityTypes = [...priorityTypes, ...remainingTypes];

    const frequentTypes = allSmartTypes
      .filter((st) => st.isFrequent || st.isCurated)
      .sort((a, b) => b.accessCount - a.accessCount);

    return {
      orderedEntityTypes,
      frequentTypes,
      allSmartTypes,
      isPersonalized,
      quickAccessCount: priorityTypes.length,
    };
  }, [subgraphId, entityTypes, tracker]);
}

export default useSmartEntityTypes;
